import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { Page, Spinner } from "../../components";
import LogoutButton from "../../components/LogoutButton";
import { useAdminSalesMetrics, useAdminSalesReps } from "../../api/hooks/admin";
import { SalesMetricDTO } from "../../api/admin/types";
import { toLocalDateString as formatDate } from "../../utils/date";

const REP_COLORS = [
  "#1b3bcc",
  "#10b981",
  "#f5a623",
  "#e74c3c",
  "#8b5cf6",
  "#06b6d4",
  "#ec4899",
  "#84cc16",
];

const SalesMetricsPage: React.FC = () => {
  const navigate = useNavigate();

  const defaultEnd = formatDate(new Date());
  const defaultStart = formatDate(
    new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
  );

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [selectedRep, setSelectedRep] = useState<string | null>(null);
  const [showNotes, setShowNotes] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);

  const { data: repsData } = useAdminSalesReps();
  const { data: metricsData, isLoading } = useAdminSalesMetrics(
    startDate,
    endDate,
    selectedRep || undefined
  );
  const metrics = metricsData?.metrics || [];

  const repOptions = useMemo(() => {
    const reps = repsData || [];
    return [
      { value: "", label: "All Reps" },
      ...reps.map((r) => ({ value: r, label: r })),
    ];
  }, [repsData]);

  // KPI computations
  const kpis = useMemo(() => {
    const totalBooked = metrics.reduce(
      (sum, m) => sum + (m.new_accounts?.num_booked || 0),
      0
    );
    const allNames = new Set(
      metrics.flatMap((m) => m.new_accounts?.names || [])
    );
    const totalNotes = metrics.reduce(
      (sum, m) => sum + (m.account_notes?.length || 0),
      0
    );
    const totalPipeline = metrics.reduce(
      (sum, m) => sum + (m.pipeline_updates?.length || 0),
      0
    );
    const activeDays = new Set(metrics.map((m) => m.date)).size;
    const avgPerDay =
      activeDays > 0 ? (totalBooked / activeDays).toFixed(1) : "0";

    return {
      totalBooked,
      uniqueNames: allNames.size,
      totalNotes,
      totalPipeline,
      activeDays,
      avgPerDay,
    };
  }, [metrics]);

  // Bar chart data: new accounts booked per day
  const barChartData = useMemo(() => {
    const dateMap: Record<string, Record<string, number>> = {};
    for (const m of metrics) {
      if (!dateMap[m.date]) dateMap[m.date] = {};
      dateMap[m.date][m.rep_name] =
        (dateMap[m.date][m.rep_name] || 0) + (m.new_accounts?.num_booked || 0);
    }
    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, reps]) => ({ date, ...reps }));
  }, [metrics]);

  const uniqueReps = useMemo(() => {
    return [...new Set(metrics.map((m) => m.rep_name))];
  }, [metrics]);

  // Line chart data: activity trends
  const lineChartData = useMemo(() => {
    const dateMap: Record<
      string,
      { newAccounts: number; accountNotes: number; pipelineUpdates: number }
    > = {};
    for (const m of metrics) {
      if (!dateMap[m.date]) {
        dateMap[m.date] = {
          newAccounts: 0,
          accountNotes: 0,
          pipelineUpdates: 0,
        };
      }
      dateMap[m.date].newAccounts += m.new_accounts?.num_booked || 0;
      dateMap[m.date].accountNotes += m.account_notes?.length || 0;
      dateMap[m.date].pipelineUpdates += m.pipeline_updates?.length || 0;
    }
    return Object.entries(dateMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({ date, ...vals }));
  }, [metrics]);

  // Sorted metrics for table (most recent first)
  const sortedMetrics = useMemo(() => {
    return [...metrics].sort((a, b) => b.date.localeCompare(a.date));
  }, [metrics]);

  return (
    <Page>
      {/* Header */}
      <div
        style={{
          width: "100%",
          maxWidth: "80vw",
          marginBottom: 24,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 16px",
          boxSizing: "border-box" as const,
          flexWrap: "wrap" as const,
          gap: 8,
          margin: "0 auto 24px",
        }}
      >
        <h1
          style={{
            fontSize: "clamp(32px, 6vw, 49px)",
            margin: 0,
            flex: "1 1 auto",
            minWidth: 0,
            fontWeight: 600,
            color: "#1a1a1a",
          }}
        >
          Internal Sales Metrics
        </h1>
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap" as const,
          }}
        >
          <button
            style={{
              padding: "8px 16px",
              borderRadius: 6,
              border: "none",
              background: "#6b7280",
              color: "#fff",
              fontSize: 14,
              fontWeight: 500,
              cursor: "pointer",
              boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
              transition: "background-color 0.2s ease",
            }}
            onClick={() => navigate("/")}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = "#4b5563";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = "#6b7280";
            }}
          >
            Back to Home
          </button>
          <LogoutButton />
        </div>
      </div>

      {/* Filter Bar */}
      <div
        style={{
          width: "100%",
          maxWidth: "80vw",
          margin: "0 auto 24px",
          padding: 20,
          border: "1px solid #e0e0e0",
          borderRadius: 8,
          backgroundColor: "#f9fafb",
          boxSizing: "border-box" as const,
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 16,
            alignItems: "flex-end",
            flexWrap: "wrap" as const,
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 180,
            }}
          >
            <label
              style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}
            >
              Start Date
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 16,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 180,
            }}
          >
            <label
              style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}
            >
              End Date
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={{
                padding: "8px 12px",
                borderRadius: 4,
                border: "1px solid #ccc",
                fontSize: 16,
              }}
            />
          </div>
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: 4,
              minWidth: 200,
            }}
          >
            <label
              style={{ fontSize: 14, fontWeight: 500, color: "#374151" }}
            >
              Sales Rep
            </label>
            <Select
              value={repOptions.find((o) => o.value === (selectedRep || ""))}
              onChange={(option) =>
                setSelectedRep(option?.value || null)
              }
              options={repOptions}
              isSearchable
              menuPortalTarget={document.body}
              menuPosition="fixed"
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: 38,
                  fontSize: 16,
                }),
                menu: (base) => ({ ...base, zIndex: 9999 }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginTop: 32,
          }}
        >
          <Spinner />
        </div>
      ) : metrics.length === 0 ? (
        <div
          style={{
            width: "100%",
            maxWidth: "80vw",
            margin: "0 auto",
            textAlign: "center",
            padding: 40,
            color: "#666",
            fontSize: 16,
          }}
        >
          No sales metrics found for the selected filters.
        </div>
      ) : (
        <div
          style={{
            width: "100%",
            maxWidth: "80vw",
            margin: "0 auto",
            display: "flex",
            flexDirection: "column",
            gap: 24,
          }}
        >
          {/* KPI Cards */}
          <div
            style={{
              display: "flex",
              gap: 16,
              flexWrap: "wrap" as const,
            }}
          >
            {[
              { label: "Accounts Booked", value: kpis.totalBooked },
              { label: "Unique Account Names", value: kpis.uniqueNames },
              { label: "Account Notes", value: kpis.totalNotes },
              { label: "Pipeline Updates", value: kpis.totalPipeline },
              { label: "Active Days", value: kpis.activeDays },
              { label: "Avg Booked/Day", value: kpis.avgPerDay },
            ].map((kpi) => (
              <div
                key={kpi.label}
                style={{
                  flex: "1 1 0",
                  minWidth: 140,
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  padding: "20px 24px",
                  background: "#fff",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
                }}
              >
                <div
                  style={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: "#1b3bcc",
                  }}
                >
                  {kpi.value}
                </div>
                <div style={{ fontSize: 14, color: "#666", marginTop: 4 }}>
                  {kpi.label}
                </div>
              </div>
            ))}
          </div>

          {/* Bar Chart: New Accounts Booked Per Day */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              New Accounts Booked Per Day
            </h3>
            <ResponsiveContainer width="100%" height={350}>
              <BarChart data={barChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                {uniqueReps.map((rep, i) => (
                  <Bar
                    key={rep}
                    dataKey={rep}
                    stackId="a"
                    fill={REP_COLORS[i % REP_COLORS.length]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Line Chart: Activity Trends */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Activity Trends Over Time
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={lineChartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="newAccounts"
                  name="New Accounts"
                  stroke="#1b3bcc"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="accountNotes"
                  name="Account Notes"
                  stroke="#10b981"
                  strokeWidth={2}
                />
                <Line
                  type="monotone"
                  dataKey="pipelineUpdates"
                  name="Pipeline Updates"
                  stroke="#f5a623"
                  strokeWidth={2}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Daily Breakdown Table */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              overflowX: "auto" as const,
            }}
          >
            <h3
              style={{
                margin: "0 0 16px 0",
                fontSize: 18,
                fontWeight: 600,
                color: "#1a1a1a",
              }}
            >
              Daily Breakdown
            </h3>
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: 14,
              }}
            >
              <thead>
                <tr
                  style={{
                    borderBottom: "2px solid #e5e7eb",
                    textAlign: "left",
                  }}
                >
                  <th style={{ padding: "8px 12px", color: "#374151" }}>
                    Date
                  </th>
                  <th style={{ padding: "8px 12px", color: "#374151" }}>
                    Rep
                  </th>
                  <th style={{ padding: "8px 12px", color: "#374151" }}>
                    Booked
                  </th>
                  <th style={{ padding: "8px 12px", color: "#374151" }}>
                    Account Names
                  </th>
                  <th style={{ padding: "8px 12px", color: "#374151" }}>
                    Notes
                  </th>
                  <th style={{ padding: "8px 12px", color: "#374151" }}>
                    Pipeline
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedMetrics.map((m) => (
                  <tr
                    key={m._id}
                    style={{ borderBottom: "1px solid #f0f0f0" }}
                  >
                    <td style={{ padding: "8px 12px" }}>{m.date}</td>
                    <td style={{ padding: "8px 12px" }}>{m.rep_name}</td>
                    <td style={{ padding: "8px 12px" }}>
                      {m.new_accounts?.num_booked || 0}
                    </td>
                    <td
                      style={{
                        padding: "8px 12px",
                        maxWidth: 300,
                        wordBreak: "break-word" as const,
                      }}
                    >
                      {m.new_accounts?.names?.join(", ") || "-"}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {m.account_notes?.length || 0}
                    </td>
                    <td style={{ padding: "8px 12px" }}>
                      {m.pipeline_updates?.length || 0}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Account Notes Section */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: showNotes ? 16 : 0,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                Account Notes ({kpis.totalNotes})
              </h3>
              <button
                onClick={() => setShowNotes(!showNotes)}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 500,
                  border: "1px solid #e0e0e0",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: showNotes ? "#3949ab" : "#fff",
                  color: showNotes ? "#fff" : "#374151",
                  transition: "all 0.15s ease",
                }}
              >
                {showNotes ? "Hide" : "Show"}
              </button>
            </div>
            {showNotes && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sortedMetrics
                  .filter((m) => m.account_notes?.length > 0)
                  .map((m) =>
                    m.account_notes.map((note, i) => (
                      <div
                        key={`${m._id}-note-${i}`}
                        style={{
                          padding: "10px 14px",
                          background: i % 2 === 0 ? "#f9fafb" : "#fff",
                          borderRadius: 6,
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          {m.date} &middot; {m.rep_name}
                        </div>
                        <div style={{ fontSize: 14 }}>
                          <strong>{note.account_name}:</strong> {note.note}
                        </div>
                      </div>
                    ))
                  )}
              </div>
            )}
          </div>

          {/* Pipeline Updates Section */}
          <div
            style={{
              border: "1px solid #e5e7eb",
              borderRadius: 12,
              padding: 24,
              background: "#fff",
              boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
              marginBottom: 40,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: showPipeline ? 16 : 0,
              }}
            >
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  color: "#1a1a1a",
                }}
              >
                Pipeline Updates ({kpis.totalPipeline})
              </h3>
              <button
                onClick={() => setShowPipeline(!showPipeline)}
                style={{
                  padding: "6px 14px",
                  fontSize: 13,
                  fontWeight: 500,
                  border: "1px solid #e0e0e0",
                  borderRadius: 6,
                  cursor: "pointer",
                  background: showPipeline ? "#3949ab" : "#fff",
                  color: showPipeline ? "#fff" : "#374151",
                  transition: "all 0.15s ease",
                }}
              >
                {showPipeline ? "Hide" : "Show"}
              </button>
            </div>
            {showPipeline && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {sortedMetrics
                  .filter((m) => m.pipeline_updates?.length > 0)
                  .map((m) =>
                    m.pipeline_updates.map((update, i) => (
                      <div
                        key={`${m._id}-pipeline-${i}`}
                        style={{
                          padding: "10px 14px",
                          background: i % 2 === 0 ? "#f9fafb" : "#fff",
                          borderRadius: 6,
                          border: "1px solid #f0f0f0",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 12,
                            color: "#999",
                            marginBottom: 4,
                          }}
                        >
                          {m.date} &middot; {m.rep_name}
                        </div>
                        <div style={{ fontSize: 14 }}>
                          {update.description}
                        </div>
                      </div>
                    ))
                  )}
              </div>
            )}
          </div>
        </div>
      )}
    </Page>
  );
};

export default SalesMetricsPage;
